/**
 * @module PromotionActivationService
 * @remarks RTK Query service for customer promotion activation data
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  CustomerPromotionActivation,
  PromotionActivationResponse,
  PromotionStatus,
} from '@models/promotions.model'
import { RootState } from '@store/store'
import { userApi } from './userApi'
import { logger } from '@/lib/utils/logger'

/**
 * Normalizes promotion status from API response to PromotionStatus enum
 * API can return either numeric values (1,2,3,4) or string keys ("readyForActivation", "activated", etc.)
 */
const normalizePromotionStatus = (status: any): PromotionStatus => {
  // If it's already a number and valid enum value, return it
  if (
    typeof status === 'number' &&
    Object.values(PromotionStatus).includes(status)
  ) {
    return status as PromotionStatus
  }

  // If it's a string key, map to numeric value
  if (typeof status === 'string') {
    switch (status.toLowerCase()) {
      case 'readyforactivation':
      case 'ready_for_activation':
        return PromotionStatus.readyForActivation
      case 'activated':
        return PromotionStatus.activated
      case 'redeemed':
        return PromotionStatus.redeemed
      case 'expired':
        return PromotionStatus.expired
      default:
        // Try to parse as number
        const parsed = parseInt(status, 10)
        if (!isNaN(parsed) && Object.values(PromotionStatus).includes(parsed)) {
          return parsed as PromotionStatus
        }
    }
  }

  // Default fallback
  return PromotionStatus.readyForActivation
}

/**
 * API service for managing promotion activation data
 */
export const PromotionActivationApi = createApi({
  reducerPath: 'promotionActivationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        headers.set('Authorization', token)
      }
      return headers
    },
  }),
  tagTypes: ['PromotionActivation'],
  endpoints: (builder) => ({
    getPromotionsActivation: builder.query<
      CustomerPromotionActivation[],
      string
    >({
             async queryFn(customerNumber, { getState }) {
         try {
           // Get promotion activation data from userApi cache only
           const state = getState() as RootState
           const queries = state.userApi?.queries || {}

          // Look for customer data that contains promotions
          for (const queryKey in queries) {
            if (
              queryKey.startsWith('getCustomerData') &&
              queries[queryKey]?.data
            ) {
              const data = queries[queryKey].data as any
              if (data?.profile?.promotions) {
                // Transform promotions to match CustomerPromotionActivation format
                const promotions = data.profile.promotions
                
                // Group by ID and keep the object with more data (the one with promotion_id)
                const groupedPromotions = new Map()
                promotions.forEach((promo: any) => {
                  const id = promo.id
                  const existing = groupedPromotions.get(id)
                  
                  // Count non-null/non-undefined properties to determine which object has more data
                  const getDataScore = (obj: any) => {
                    return Object.values(obj).filter(value => 
                      value !== null && value !== undefined && value !== ''
                    ).length
                  }
                  
                  // If no existing entry OR current promo has more data, use current promo
                  if (!existing || getDataScore(promo) > getDataScore(existing)) {
                    groupedPromotions.set(id, promo)
                  }
                })
                                
                const formattedActivations: CustomerPromotionActivation[] = 
                  Array.from(groupedPromotions.values()).map((promo: any) => {
                    const normalizedStatus = normalizePromotionStatus(
                      promo.status
                    )

                    return {
                      id: promo.id,
                      md: 0,
                      kundenpromotions: {
                        promotion_id: promo.promotion_id,
                        kunde: { id: customerNumber },
                        redeemed: promo.redeemed,
                        kundenbetreuung: promo.kundenbetreuung,
                        ja_nein: promo.ja_nein,
                        text: promo.text,
                        valid: promo.valid,
                        active: promo.active,
                        required_points: promo.required_points,
                        list_points: promo.list_points,
                        latest_validation: promo.latest_validation,
                        activated: promo.activated,
                        status: normalizedStatus,
                      },
                    }
                  })

                return { data: formattedActivations }
              }
            }
          }

          // No cache data available - return empty array (userApi should be called first)
          return { data: [] }
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: String(error),
              error: String(error),
            } as any,
          }
        }
      },
      providesTags: ['PromotionActivation'],
    }),
    activatePromotion: builder.mutation<
      PromotionActivationResponse,
      {
        customerPromotionId: string
        customerNumber: string
        active: boolean
        status: PromotionStatus
      }
    >({
      query: ({ customerPromotionId, customerNumber, active, status }) => {
        const currentDate = new Date().toISOString()

        return {
          url: `data/kundenpromotions/${customerPromotionId}`,
          method: 'PATCH',
          body: {
            where: {
              'kunde.Kundennummer': customerNumber,
            },
            fields: {
              activated: currentDate,
              active: active,
              status: status,
            },
          },
        }
      },
                   invalidatesTags: ['PromotionActivation'],
      async onQueryStarted(
        { customerPromotionId, customerNumber, active, status },
        { dispatch, queryFulfilled }
      ) {
        logger.info("Aktivieren der Promotion", { customerPromotionId, customerNumber, active, status });
        try {
          await queryFulfilled
          // Invalidate userApi cache to ensure fresh data on page reload
          dispatch(
            userApi.util.invalidateTags([{ type: 'CustomerProfile' }])
          )
        } catch (error) {
          logger.error("Fehler beim Aktivieren der Promotion", error);
          // Mutation failed, no need to invalidate cache
        }
      },
    }),
  }),
})

// Export hooks für Verwendung in Komponenten
export const { useGetPromotionsActivationQuery, useActivatePromotionMutation } =
  PromotionActivationApi
